import { useState } from "react";
import {
  Button,
  Card,
  Chip,
  Link,
  Tooltip,
  useTheme,
  Stack,
  Box,
  Typography,
} from "@mui/material";
import { East, CheckCircle } from "@mui/icons-material";
import { getPartnerLogo } from "../../service/logo.service";
import { usePlanPrice, useSelectInsurance, useTripDays } from "../../hooks/plan.hooks";
import CoverageDialog from "../coverage-dialog/coverage-dialog.component";
import { maxInstallments } from "../../config/installments";
import CompareToggle from "../compare-toggle/compare-toggle.component";
import {
  coversCovid,
  formatBRL,
  formatLuggageCoverage,
  formatMedicalCoverage,
  getLuggageText,
  isStudentOnly,
  luggageCoverageValue,
  meetsSchengen,
} from "../../utils/coverage.utils";

const TEXT_MAX_LENGTH = 70;

// Selo "✓ Schengen" (cobertura mínima de €30 mil exigida na Europa).
export const SchengenChip = () => (
  <Tooltip title="Atende à exigência do Espaço Schengen: cobertura médica mínima de €30 mil.">
    <Chip
      icon={<CheckCircle />}
      label="Atende Schengen"
      size="small"
      color="success"
      variant="outlined"
      sx={{ fontWeight: 600, width: "fit-content" }}
    />
  </Tooltip>
);

// Card de um plano na lista de resultados (formato compacto, em linha).
const InsuranceOffer = ({ insurance, isEurope, highlightLabel }) => {
  const theme = useTheme();
  const selectInsurance = useSelectInsurance();
  const { total, perDay, perPerson, passengers, mixedAges } = usePlanPrice(insurance);
  const [coverageModalOpen, setCoverageModalOpen] = useState(false);

  if (!insurance) {
    return null;
  }

  const {
    insuranceCompany,
    insuranceName,
    ageInfo,
    healthCoverage,
    coverageList,
  } = insurance;
  const luggageCoverage = getLuggageText(insurance);
  const hasLuggage = luggageCoverageValue(luggageCoverage) > 0;

  const isAgeInfoTextTooLong = ageInfo?.length > TEXT_MAX_LENGTH;
  const ageInfoText = isAgeInfoTextTooLong
    ? ageInfo.slice(0, TEXT_MAX_LENGTH).trim() + "..."
    : ageInfo;

  const labelSx = { color: "text.secondary", fontSize: "0.8rem" };

  return (
    <Card
      elevation={0}
      sx={{
        position: "relative",
        overflow: "visible",
        backgroundColor: theme.palette.grey[50],
        border: "1px solid",
        borderColor: highlightLabel ? "primary.main" : "transparent",
        borderRadius: theme.spacingValues.medium,
        p: { xs: 2, md: 2.5 },
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "150px 1.2fr 1.2fr 1fr" },
        gap: { xs: 1.5, md: 3 },
        alignItems: "center",
      }}
    >
      {highlightLabel && (
        <Chip
          label={highlightLabel}
          size="small"
          color="primary"
          sx={{ position: "absolute", top: -12, left: 16, fontWeight: 700 }}
        />
      )}

      {/* Seguradora + plano (no celular ficam lado a lado) */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
        <Box
          sx={{
            height: { xs: 36, md: 48 },
            width: { xs: 110, md: 150 },
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            "& svg": { height: "100%", width: "100%" },
          }}
        >
          {getPartnerLogo(insuranceCompany)?.component.dark}
        </Box>
        <Typography
          sx={{ display: { xs: "block", md: "none" }, fontWeight: 700, color: "primary.main" }}
        >
          {insuranceName}
        </Typography>
      </Stack>

      <Stack spacing={0.75} sx={{ minWidth: 0 }}>
        <Typography
          sx={{ display: { xs: "none", md: "block" }, fontWeight: 700, color: "primary.main" }}
        >
          {insuranceName}
        </Typography>
        <Tooltip title={isAgeInfoTextTooLong ? ageInfo : null}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {ageInfoText}
          </Typography>
        </Tooltip>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Covid-19: {coversCovid(insurance) ? "coberto" : "não coberto"}
        </Typography>
        {isStudentOnly(insurance) && (
          <Chip
            label="Exclusivo para estudantes"
            size="small"
            color="warning"
            variant="outlined"
            sx={{ fontWeight: 600, width: "fit-content" }}
          />
        )}
        {isEurope && meetsSchengen(healthCoverage) && <SchengenChip />}
      </Stack>

      <Stack spacing={0.25}>
        <Typography sx={labelSx}>Despesas médicas</Typography>
        <Tooltip title={healthCoverage}>
          <Typography
            sx={{ fontWeight: 800, fontSize: { xs: "1.3rem", md: "1.45rem" }, color: "text.primary", lineHeight: 1.2 }}
          >
            {formatMedicalCoverage(healthCoverage, isEurope)}
          </Typography>
        </Tooltip>
        <Typography sx={{ ...labelSx, pt: 0.75 }}>
          Bagagem:{" "}
          <Tooltip title={luggageCoverage || ""}>
            <strong>
              {hasLuggage ? formatLuggageCoverage(luggageCoverage, isEurope) : "não coberto"}
            </strong>
          </Tooltip>
        </Typography>
        {coverageList?.length > 0 && (
          <Link
            component="button"
            variant="body2"
            onClick={() => setCoverageModalOpen(true)}
            sx={{ width: "fit-content", pt: 0.5, textAlign: "left" }}
          >
            Ver cobertura completa
          </Link>
        )}
      </Stack>

      <Stack spacing={0.5} alignItems={{ xs: "stretch", md: "flex-end" }}>
        <Typography sx={{ ...labelSx, textAlign: { md: "right" } }}>
          Total {passengers > 1 ? `para ${passengers} pessoas` : ""}
        </Typography>
        <Typography
          sx={{ fontWeight: 800, fontSize: "1.5rem", color: "text.primary", lineHeight: 1.1, textAlign: { md: "right" } }}
        >
          {formatBRL(total)}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", textAlign: { md: "right" } }}>
          {perDay > 0 && <>{formatBRL(perDay)}/dia</>}
          {passengers > 1 && (
            <> · {mixedAges ? "média de " : ""}{formatBRL(perPerson)}/pessoa</>
          )}
          <br />
          em até {maxInstallments(total)}x sem juros
        </Typography>
        <Button
          variant="contained"
          onClick={() => selectInsurance(insurance)}
          endIcon={<East sx={{ transform: "rotate(-45deg)" }} />}
          sx={{ mt: 1, whiteSpace: "nowrap" }}
        >
          Selecionar
        </Button>
        <Box sx={{ alignSelf: { xs: "center", md: "flex-end" } }}>
          <CompareToggle insurance={insurance} />
        </Box>
      </Stack>

      <CoverageDialog
        insurance={insurance}
        open={coverageModalOpen}
        onClose={() => setCoverageModalOpen(false)}
      />
    </Card>
  );
};

export default InsuranceOffer;

// Reexporta para quem já importava daqui.
export { CoverageDialog, usePlanPrice, useSelectInsurance, useTripDays };
